import BigNumber from 'bignumber.js';
import React, {useEffect, useMemo, useRef} from 'react';

// ========================================================================
// Formatter
// ========================================================================

type AmountFormatterOptions = Readonly<{
    maxIntegers: number;
    maxDecimals: number;
    minDecimals?: number | undefined;
}>;

type Formatter = ReturnType<typeof createAmountInputFormatter>;

export const createAmountInputFormatter = ({maxIntegers, maxDecimals, minDecimals = 0}: AmountFormatterOptions) => {
    const leadingZeros = /^0+(?=[0-9])/;
    const trailingZeros = /([1-9]*)0+$/;

    const isCorrect = createCorrectnessCheck({maxIntegers, maxDecimals});

    return {
        preProcess(value: RsnAmount, previousValue: RsnAmount): RsnAmount {
            const integers = value.integers.replace(leadingZeros, '');
            const maybeDot = value.decimals[0] || '';
            const decimals = value.decimals[1]?.slice(0, maxDecimals);
            const nextValue: RsnAmount = maybeDot
                ? {integers, decimals: [maybeDot, decimals]}
                : {integers, decimals: []};
            if (isCorrect(RsnAmount.toString(nextValue))) {
                return nextValue;
            }
            return previousValue;
        },
        postProcess(value: RsnAmount): RsnAmount {
            const integers = value.integers;
            const maybeDot = value.decimals[0];
            const decimals = value.decimals[1]?.replace(trailingZeros, '$1')?.padEnd(minDecimals, '0');

            if (maybeDot && decimals) {
                return {integers: integers, decimals: maybeDot ? [maybeDot, decimals] : []};
            }
            if (minDecimals === 0) {
                return {integers, decimals: []};
            } else {
                return {integers, decimals: ['.', '0'.repeat(minDecimals)]};
            }
        },
    };
};

function createCorrectnessCheck({maxIntegers, maxDecimals}: AmountFormatterOptions) {
    const integersGroup = `^(?:0|[1-9][0-9]{0,${maxIntegers - 1}})?`;
    const decimalsGroup = `(?:\\.[0-9]{0,${maxDecimals}})?$`;

    const shouldNotFraction = maxDecimals === 0;
    // eslint-disable-next-line security/detect-non-literal-regexp
    const correctShape = new RegExp(shouldNotFraction ? `${integersGroup}$` : integersGroup + decimalsGroup);

    return RegExp.prototype.test.bind(correctShape);
}

export function toBigNumber(amountInput: string | RsnAmount) {
    if (typeof amountInput !== 'string') {
        return BigNumber(RsnAmount.toString(amountInput));
    }
    if (amountInput.startsWith('.')) {
        amountInput = '0' + amountInput;
    }
    const bn = BigNumber(amountInput || 0);
    if (bn.isNaN()) {
        return BigNumber(0);
    } else {
        return bn;
    }
}

// ========================================================================
// React Utils
// ========================================================================

export const useAmountFormatter = (options: AmountFormatterOptions) =>
    useMemo(createAmountInputFormatter.bind(null, options), Object.values(options));

export function useAmountInputControls(
    ...args: [formatter: Formatter, value: RsnAmount, changeHandler: (value: RsnAmount) => void]
) {
    const [formatter, value, changeHandler] = args;
    const lastValueRef = useRef<RsnAmount>(value);
    const lastSuccessfullyParsedValueRef = useRef<RsnAmount>(value);

    const inputRef = useRef<HTMLInputElement>(null);

    const setValue = (newValue: RsnAmount) => {
        changeHandler((lastValueRef.current = newValue));
    };

    const onChange = (rawEnteredValue: string) => {
        const input = inputRef.current;
        if (!input) {
            console.debug('%c The inputRef is not passed to input element', 'color:red;');
            return;
        }

        try {
            lastSuccessfullyParsedValueRef.current = RsnAmount.fromString(rawEnteredValue);
        } catch (_err: unknown) {
            input.dispatchEvent(new Event('invalid'));
        }

        const parsedAmount = lastSuccessfullyParsedValueRef.current;
        const lastValue = lastValueRef.current;
        const nextValue = formatter.preProcess(parsedAmount, lastValue);

        if (!RsnAmount.equals(parsedAmount, lastValue) && RsnAmount.equals(nextValue, lastValue)) {
            // user entered different value but it was replaced with lastValue
            input.dispatchEvent(new Event('invalid'));
        }

        if (!RsnAmount.equals(parsedAmount, nextValue)) {
            // react will reset input element value, need to restore caret position
            const selectionStart = input.selectionStart ?? 0;
            requestAnimationFrame(function restoreSelection() {
                const diffLength = rawEnteredValue.length - RsnAmount.toString(nextValue).length;
                const cursorPosition = Math.max(0, selectionStart - diffLength);
                input.selectionStart = input.selectionEnd = cursorPosition;
            });
        }

        setValue(nextValue);
    };

    const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setValue(formatter.postProcess(RsnAmount.fromString(event.target.value)));
    };

    useEffect(() => {
        if (!RsnAmount.equals(lastValueRef.current, value)) {
            // value changed but lastValue didn't, this can only mean that new value was set outside
            setValue(formatter.preProcess(value, lastValueRef.current));
        }
    }, [useAmountMemo(value)]);

    return {
        ref: inputRef,
        value: RsnAmount.toString(lastValueRef.current),
        onChange,
        onBlur,
    };
}

// ========================================================================
// Amount
// ========================================================================

function useAmountMemo(amount: RsnAmount) {
    return useMemo(() => amount, [amount.integers, amount.decimals[0], amount.decimals[1]]);
}

export interface RsnAmount {
    integers: string;
    decimals: [] | [dot: '.', digits?: string];
}

export namespace RsnAmount {
    export function empty(): RsnAmount {
        return {
            integers: '',
            decimals: [],
        };
    }

    export const useMemo = useAmountMemo;

    export function toString(amount: RsnAmount) {
        const integers = amount.integers;
        const decimals = amount.decimals;

        return `${integers}${decimals.join('')}`;
    }

    type DigitCode = number & {readonly brand: unique symbol};
    function isDigitCode(num: number): num is DigitCode {
        return '0'.charCodeAt(0) <= num && num <= '9'.charCodeAt(0);
    }
    function isDelimiter(char: number) {
        const internalinalGroupDelimiters = [160, 44, 46, 8217, 11841, 8239, 1548];
        return internalinalGroupDelimiters.indexOf(char) !== -1;
    }
    export function fromString(anyString: string): RsnAmount {
        if (anyString.toLowerCase().includes('e')) {
            throw new Error(`Invalid value: ${anyString}. Scientific notation is not supported.`);
        }
        let decimals: DigitCode[] = [];
        let integers: DigitCode[] = [];
        let hasDot = false;
        let idx = anyString.length - 1;
        for (; idx >= 0; idx--) {
            const char = anyString.charCodeAt(idx);
            if (isDelimiter(char)) {
                hasDot = true;
                decimals = integers;
                integers = [];
                break;
            }
            if (isDigitCode(char)) {
                integers.unshift(char);
            }
        }
        for (; idx >= 0; idx--) {
            const char = anyString.charCodeAt(idx);
            if (isDigitCode(char)) {
                integers.unshift(char);
            }
        }
        if (!hasDot) {
            return {integers: String.fromCharCode(...integers), decimals: []};
        }
        if (decimals.length) {
            return {integers: String.fromCharCode(...integers), decimals: ['.', String.fromCharCode(...decimals)]};
        } else {
            return {integers: String.fromCharCode(...integers), decimals: ['.']};
        }
    }

    export function toNumber(value: RsnAmount) {
        const stringVal = toString(value);
        if (stringVal === '' || stringVal === '.') {
            return Number(0);
        }
        return Number(stringVal);
    }

    export function fromNumber(value: number) {
        return fromString(value.toString());
    }

    export function fromBigNumber(value: BigNumber) {
        return fromString(value.toString());
    }

    export function toBigNumber(value: RsnAmount) {
        const stringVal = toString(value);
        if (stringVal === '' || stringVal === '.') {
            return BigNumber(0);
        }
        return BigNumber(stringVal);
    }

    export function validateMin(min: BigNumber.Value, message: string) {
        return (amount: RsnAmount) => {
            if (toBigNumber(amount).isLessThan(min)) {
                return message;
            }
            return undefined;
        };
    }

    export function validateMinExclusive(min: BigNumber.Value, message: string) {
        return (amount: RsnAmount) => {
            if (toBigNumber(amount).isLessThanOrEqualTo(min)) {
                return message;
            }
            return undefined;
        };
    }

    export function validateMax(max: BigNumber.Value, message: string) {
        return (amount: RsnAmount) => {
            if (toBigNumber(amount).isGreaterThan(max)) {
                return message;
            }
            return undefined;
        };
    }

    export function equals(a: RsnAmount, b: RsnAmount) {
        return (
            a.integers === b.integers &&
            a.decimals.length === b.decimals.length &&
            a.decimals.every((value, index) => value === b.decimals[index])
        );
    }
}

if (import.meta.vitest) {
    const {test, expect} = import.meta.vitest;

    test('RsnAmount isomorphism', () => {
        let input = '';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '0';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '0.';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '0.0';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '0.01';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '0.5';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '3';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '3.';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '3.0';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '3.01';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
        input = '3.5';
        expect(RsnAmount.toString(RsnAmount.fromString(input))).toEqual(input);
    });
}
